import { Injectable } from '@angular/core';
import {
  AiBillExtraction,
  ApiElectricityBillingInformation,
  ApiGasBillingInformation,
  StringAiValue,
  MoneyAiValue,
  NumberAiValue,
  DateAiValue,
  ChargeBucket as ApiChargeBucket,
  ChargeItem as ApiChargeItem
} from './api-models';
import { ParsedBillData, ChargeGroup, ChargeLineItem, NameValueIdentifier } from '../models';
import { CommodityType, isCommodityType } from '../../zentility/_shared/_zen-legacy-common/_models/commodity';
import { platformGasUnitName } from '../utils/gas-unit.util';

/**
 * The ParsedBillData fields that come from the commodity-specific part of an
 * extraction: usage, demand and the charge buckets. Everything else is shared.
 */
type CommodityFields = Pick<ParsedBillData,
  | 'usageAmount' | 'rawUsageAmount' | 'usageUnit'
  | 'demandKw' | 'rawDemandKw'
  | 'onPeakUsage' | 'intermediateUsage' | 'offPeakUsage'
  | 'onPeakCost' | 'intermediateCost' | 'offPeakCost'
  | 'supplyContractCharges' | 'supplyEnergyCharges' | 'supplyCapacityCharges' | 'supplyTransmissionCharges'
  | 'supplyMarketCharges' | 'supplyLineLossCharges' | 'supplyGrtSut' | 'supplyMiscCharges'
  | 'supplyCustomerCharges' | 'supplyGasCharges' | 'supplyOffPeakCharges' | 'supplyRateClassCharges'
  | 'distributionCustomerCharges' | 'distributionEnergyCharges' | 'distributionDemandCharges' | 'distributionMiscCharges'
  | 'distributionGasCharges' | 'distributionDeliveryCharges' | 'distributionOffPeakCharges'
  | 'distributionBalancingCharges' | 'distributionRateClassCharges' | 'distributionGrtSut'
>;

/**
 * Reads AI values into plain values, recording each field's confidence under its
 * ParsedBillData field name so the overall confidence can be averaged at the end.
 *
 * - StringAiValue: text fields (value, confidence)
 * - MoneyAiValue: monetary amounts (value, confidence, textValue, currencyCode)
 * - NumberAiValue: numeric values (value, confidence, textValue)
 * - DateAiValue: dates (value as ISO string, confidence, textValue)
 */
class AiValueReader {
  readonly confidenceMap: Record<string, number> = {};

  string(field: StringAiValue | undefined, fieldName: string): string {
    this.record(field, fieldName);
    return field?.value ?? '';
  }

  money(field: MoneyAiValue | undefined, fieldName: string): number {
    this.record(field, fieldName);
    return field?.value ?? 0;
  }

  number(field: NumberAiValue | undefined, fieldName: string): number {
    this.record(field, fieldName);
    return field?.value ?? 0;
  }

  date(field: DateAiValue | undefined, fieldName: string): string {
    this.record(field, fieldName);
    return field?.value ?? '';
  }

  /** Original text from the document (Money, Number and Date values carry one) */
  text(field: MoneyAiValue | NumberAiValue | DateAiValue | undefined): string | undefined {
    return field?.textValue ?? undefined;
  }

  private record(field: { confidence?: number } | undefined, fieldName: string): void {
    if (field?.confidence !== undefined) {
      this.confidenceMap[fieldName] = field.confidence;
    }
  }
}

function assertNever(value: never): never {
  throw new Error(`Unsupported bill extraction commodity: ${JSON.stringify(value)}`);
}

/**
 * Maps the API's per-commodity bill extraction (ElectricityBillExtraction |
 * GasBillExtraction, see api-models.ts) onto the flat ParsedBillData the bill
 * form and persistence work with.
 */
/**
 * A time-of-use period's cost as printed on the bill, including an explicit $0.00 (a credit
 * or free tier is a real value). Undefined when the bill prices nothing for the period, so
 * the editor's cost field stays blank rather than reading $0.00. The per-period rate is never
 * extracted; the editor and the API derive it from cost / usage.
 */
function periodCost(cost: MoneyAiValue | null | undefined, read: AiValueReader, field: string): number | undefined {
  return cost?.value != null ? read.money(cost, field) : undefined;
}

function gasUsageUnit(printed: string | null | undefined): string {
  const text = printed?.trim();
  if (!text) return 'Dth';
  return platformGasUnitName(text) ?? text;
}

@Injectable({
  providedIn: 'root'
})
export class BillMappingService {

  /**
   * Maps AI bill extraction API response to ParsedBillData model.
   *
   * @param fallbackCommodity commodity of the response wrapper, used only when the
   *   extraction itself carries none (raw extractions persisted before the API wrote it)
   */
  mapApiResponseToParsedBillData(apiData: AiBillExtraction, fallbackCommodity?: CommodityType): ParsedBillData {
    const extraction = BillMappingService.withCommodity(apiData, fallbackCommodity);
    const read = new AiValueReader();

    const legalEntity = extraction.legalEntity;
    const contractInfo = extraction.contractInformation;
    const billingInfo = extraction.billingInformation;

    // Map legal entity
    const legalEntityName = read.string(legalEntity?.name, 'legalEntity');

    // Map service address
    const serviceAddress = legalEntity?.serviceAddress;
    const serviceAddressStreet1 = read.string(serviceAddress?.street1, 'serviceAddressStreet1');
    const serviceAddressStreet2 = read.string(serviceAddress?.street2, 'serviceAddressStreet2');
    const serviceAddressCity = read.string(serviceAddress?.city, 'serviceAddressCity');
    const serviceAddressState = read.string(serviceAddress?.state, 'serviceAddressState');
    const serviceAddressZip = read.string(serviceAddress?.postalCode, 'serviceAddressZip');

    // Map billing address
    const billingAddress = legalEntity?.billingAddress;
    const billingAddressName1 = read.string(billingAddress?.name1, 'billingAddressName1');
    const billingAddressName2 = read.string(billingAddress?.name2, 'billingAddressName2');
    const billingAddressStreet1 = read.string(billingAddress?.street1, 'billingAddressStreet1');
    const billingAddressStreet2 = read.string(billingAddress?.street2, 'billingAddressStreet2');
    const billingAddressCity = read.string(billingAddress?.city, 'billingAddressCity');
    const billingAddressState = read.string(billingAddress?.state, 'billingAddressState');
    const billingAddressZip = read.string(billingAddress?.postalCode, 'billingAddressZip');

    // Map contract information — supplier is a resolved ObjectAiValue<SupplierIdAndName>
    const supplierObj = contractInfo?.supplier;
    const supplierId = supplierObj?.value?.id ?? undefined;
    const providerName = supplierObj?.value?.name || 'On Utility';
    if (supplierObj?.confidence !== undefined) {
      read.confidenceMap['providerName'] = supplierObj.confidence;
    }
    const rateType = contractInfo?.rateType?.value ?? null;
    const rateTypeConfidence = contractInfo?.rateType?.confidence;
    const contractEndDate = read.date(contractInfo?.endDate, 'contractEndDate');
    const rawContractEndDate = read.text(contractInfo?.endDate);

    // Map billing information — utility is a resolved ObjectAiValue<UtilityIdAndName>
    const utilityObj = billingInfo?.utility;
    const utilityId = utilityObj?.value?.id ?? undefined;
    const utilityName = utilityObj?.value?.name || '';
    if (utilityObj?.confidence !== undefined) {
      read.confidenceMap['utilityName'] = utilityObj.confidence;
    }
    const utilityAccountNumber = read.string(billingInfo?.utilityAccountNumber, 'utilityAccountNumber');
    const rateClass = read.string(billingInfo?.rateClass, 'rateClass') || undefined;

    // Map additional identifiers (convert from API array to frontend array)
    const additionalIdentifiers: NameValueIdentifier[] = (billingInfo?.additionalIdentifiers || []).map(id => ({
      name: id.name,
      value: id.value,
      confidence: id.confidence ?? 0
    }));

    // UAN2/UAN3 are assigned server-side using the utility_account_number table (accountOrder + regex).
    // If the server didn't set them (no utility matched), leave empty — the meter
    // step will re-map via POST /v4/.../bill-import/map-identifiers once the user
    // chooses a utility. Log when we have additionalIdentifiers but no UAN2/3
    // so the gap is visible in test/dev envs.
    const utilityAccountNumber2 = read.string(billingInfo?.utilityAccountNumber2, 'utilityAccountNumber2');
    const utilityAccountNumber3 = read.string(billingInfo?.utilityAccountNumber3, 'utilityAccountNumber3');
    if (!utilityAccountNumber2 && !utilityAccountNumber3 && additionalIdentifiers.length > 0) {
      console.debug(
        '[BillMapping] Server returned no UAN2/UAN3 despite',
        additionalIdentifiers.length, 'additionalIdentifiers — likely no utility matched yet.'
      );
    }

    // Map billing period
    const billingPeriodStart = read.date(billingInfo?.billPeriodStart, 'billingPeriodStart');
    const billingPeriodEnd = read.date(billingInfo?.billPeriodEnd, 'billingPeriodEnd');

    // Map amounts
    const totalAmount = read.money(billingInfo?.periodBillTotal, 'totalAmount');
    const rawTotalAmount = read.text(billingInfo?.periodBillTotal);
    const currency = billingInfo?.periodBillTotal?.currencyCode || 'USD';

    // Map matched entities from hierarchical matching (if present)
    const matchedCustomerObj = legalEntity?.matchedCustomer;
    const matchedCustomer = matchedCustomerObj?.value ? {
      id: matchedCustomerObj.value.id,
      name: matchedCustomerObj.value.name,
      confidence: matchedCustomerObj.confidence ?? 0
    } : undefined;

    const matchedLenObj = legalEntity?.matchedLen;
    const matchedLen = matchedLenObj?.value ? {
      id: matchedLenObj.value.id,
      name: matchedLenObj.value.name,
      confidence: matchedLenObj.confidence ?? 0
    } : undefined;

    const matchedServiceAddressObj = legalEntity?.matchedServiceAddress;
    const matchedServiceAddress = matchedServiceAddressObj?.value ? {
      id: matchedServiceAddressObj.value.id,
      formattedAddress: matchedServiceAddressObj.value.formattedAddress,
      confidence: matchedServiceAddressObj.confidence ?? 0
    } : undefined;

    const matchedMeterObj = billingInfo?.matchedMeter;
    const matchedMeter = matchedMeterObj?.value ? {
      id: matchedMeterObj.value.id,
      utilityAccountNum1: matchedMeterObj.value.utilityAccountNum1,
      confidence: matchedMeterObj.confidence ?? 0
    } : undefined;

    // Supply and distribution totals, and the state tax bucket, are on the shared
    // base of both commodities; the charge buckets are mapped per commodity below.
    const supply = billingInfo?.supply;
    const supplyTotalCost = read.money(supply?.totalCost, 'supplyTotalCost');
    const supplyCharges = read.money(supply?.supplyCharges, 'supplyCharges');
    const supplyTaxes = read.money(supply?.stateTaxes?.total?.cost, 'supplyTaxes');
    const rawSupplyTotalCost = read.text(supply?.totalCost);
    const rawSupplyCharges = read.text(supply?.supplyCharges);
    const supplySalesTaxes = this.mapChargeBucket(supply?.stateTaxes);

    const distribution = billingInfo?.distribution;
    const distributionTotalCost = read.money(distribution?.totalCost, 'distributionTotalCost');
    const distributionCharges = read.money(distribution?.distributionCharges, 'distributionCharges');
    const distributionTaxes = read.money(distribution?.stateTaxes?.total?.cost, 'distributionTaxes');
    const rawDistributionTotalCost = read.text(distribution?.totalCost);
    const rawDistributionCharges = read.text(distribution?.distributionCharges);
    const distributionTaxCharges = this.mapChargeBucket(distribution?.stateTaxes);

    const commodityFields = this.mapCommodityFields(extraction, read);

    // Calculate overall confidence (average of all non-null field confidences)
    const confidence = this.calculateOverallConfidence(read.confidenceMap);

    // Build the parsed bill data
    const parsedData: ParsedBillData = {
      commodityType: extraction.commodityType,

      // Legal Entity
      legalEntity: legalEntityName,

      // Service Address
      serviceAddressStreet1,
      serviceAddressStreet2,
      serviceAddressCity,
      serviceAddressState,
      serviceAddressZip,

      // Billing Address
      billingAddressName1,
      billingAddressName2,
      billingAddressStreet1,
      billingAddressStreet2,
      billingAddressCity,
      billingAddressState,
      billingAddressZip,

      // Contract Information
      supplierId,
      providerName,
      rateType,
      rateTypeConfidence,
      contractEndDate,
      rawContractEndDate,

      // Billing Information
      utilityId,
      utilityName,
      utilityAccountNumber,
      rateClass,
      utilityAccountNumber2,
      utilityAccountNumber3,
      utilityAccountNumberLabel: billingInfo?.utilityAccountNumberLabel,
      utilityAccountNumber2Label: billingInfo?.utilityAccountNumber2Label,
      utilityAccountNumber3Label: billingInfo?.utilityAccountNumber3Label,
      additionalIdentifiers,

      billingPeriodStart,
      billingPeriodEnd,

      totalAmount,
      rawTotalAmount,
      currency,

      // Supply totals
      supplyTotalCost,
      supplyCharges,
      supplyTaxes,
      rawSupplyTotalCost,
      rawSupplyCharges,
      supplySalesTaxes,

      // Distribution totals
      distributionTotalCost,
      distributionCharges,
      distributionTaxes,
      rawDistributionTotalCost,
      rawDistributionCharges,
      distributionTaxCharges,

      // Usage, demand and charge buckets — the commodity's own
      ...commodityFields,

      // Matched entities (from hierarchical matching)
      matchedCustomer,
      matchedLen,
      matchedServiceAddress,
      matchedMeter,

      // Metadata
      billType: 'utility', // Assume electric/utility for now
      confidence,
      fieldConfidence: read.confidenceMap
    };

    return parsedData;
  }

  /**
   * The API has written commodityType on every extraction since gas support landed.
   * Raw extractions persisted by sessions from before then carry none; those were
   * electricity unless the response wrapper said otherwise. Stamp that in so the
   * union can be narrowed from here on.
   */
  private static withCommodity(apiData: AiBillExtraction, fallbackCommodity?: CommodityType): AiBillExtraction {
    if (isCommodityType(apiData.commodityType)) {
      return apiData;
    }
    const commodityType = fallbackCommodity === CommodityType.Gas ? CommodityType.Gas : CommodityType.Electricity;
    return { ...apiData, commodityType } as AiBillExtraction;
  }

  /**
   * Usage, demand and the charge buckets belong to one commodity or the other.
   * Narrowing on commodityType holds each branch to its own commodity's fields.
   */
  private mapCommodityFields(extraction: AiBillExtraction, read: AiValueReader): CommodityFields {
    switch (extraction.commodityType) {
      case CommodityType.Electricity:
        return this.mapElectricityFields(extraction.billingInformation, read);
      case CommodityType.Gas:
        return this.mapGasFields(extraction.billingInformation, read);
      default:
        return assertNever(extraction);
    }
  }

  private mapElectricityFields(billingInfo: ApiElectricityBillingInformation, read: AiValueReader): CommodityFields {
    const supply = billingInfo?.supply;
    const distribution = billingInfo?.distribution;
    return {
      usageAmount: read.number(billingInfo?.totalBilledUsageKwh, 'usageAmount'),
      rawUsageAmount: read.text(billingInfo?.totalBilledUsageKwh),
      usageUnit: 'kWh',

      demandKw: read.number(billingInfo?.demandKw, 'demandKw'),
      rawDemandKw: read.text(billingInfo?.demandKw),

      // Time-of-use breakdown of the usage; absent when the bill prints none
      onPeakUsage: read.number(billingInfo?.onPeakUsage, 'onPeakUsage') || undefined,
      intermediateUsage: read.number(billingInfo?.intermediateUsage, 'intermediateUsage') || undefined,
      offPeakUsage: read.number(billingInfo?.offPeakUsage, 'offPeakUsage') || undefined,
      // What each period cost, as printed; unset when the bill prices nothing for the period.
      // The editor derives the rate from cost / usage, and the import stores it the same way.
      onPeakCost: periodCost(billingInfo?.onPeakCost, read, 'onPeakCost'),
      intermediateCost: periodCost(billingInfo?.intermediateCost, read, 'intermediateCost'),
      offPeakCost: periodCost(billingInfo?.offPeakCost, read, 'offPeakCost'),

      // Charge buckets (budgeting model only)
      supplyContractCharges: this.mapChargeBucket(supply?.contractCharges),
      supplyEnergyCharges: this.mapChargeBucket(supply?.energyCharges),
      supplyCapacityCharges: this.mapChargeBucket(supply?.capacityCharges),
      supplyTransmissionCharges: this.mapChargeBucket(supply?.transmissionCharges),
      supplyMarketCharges: this.mapChargeBucket(supply?.marketCharges),
      supplyLineLossCharges: this.mapChargeBucket(supply?.lineLossCharges),
      supplyGrtSut: this.mapChargeBucket(supply?.grtSut),
      supplyMiscCharges: this.mapChargeBucket(supply?.misc),

      distributionCustomerCharges: this.mapChargeBucket(distribution?.customerCharges),
      distributionEnergyCharges: this.mapChargeBucket(distribution?.energyCharges),
      distributionDemandCharges: this.mapChargeBucket(distribution?.demandCharges),
      distributionMiscCharges: this.mapChargeBucket(distribution?.misc)
    };
  }

  private mapGasFields(billingInfo: ApiGasBillingInformation, read: AiValueReader): CommodityFields {
    const supply = billingInfo?.supply;
    const distribution = billingInfo?.distribution;
    return {
      usageAmount: read.number(billingInfo?.totalBilledUsage, 'usageAmount'),
      rawUsageAmount: read.text(billingInfo?.totalBilledUsage),
      // The unit as printed on the bill, normalised to the platform name the editor's
      // dropdown binds by ("therms" → "Th"); Dth when the bill did not say. An
      // unrecognised unit is kept as printed so the dropdown stays blank for the user
      // to pick, rather than guessing.
      usageUnit: gasUsageUnit(billingInfo?.usageUnit?.value),

      // Charge buckets (budgeting model only)
      supplyContractCharges: this.mapChargeBucket(supply?.contractCharges),
      supplyGasCharges: this.mapChargeBucket(supply?.gasCharges),
      supplyCustomerCharges: this.mapChargeBucket(supply?.customerCharges),
      supplyOffPeakCharges: this.mapChargeBucket(supply?.offPeakCharges),
      supplyRateClassCharges: this.mapChargeBucket(supply?.rateClassCharges),
      supplyGrtSut: this.mapChargeBucket(supply?.grtSut),
      supplyMiscCharges: this.mapChargeBucket(supply?.misc),

      distributionCustomerCharges: this.mapChargeBucket(distribution?.customerCharges),
      distributionDeliveryCharges: this.mapChargeBucket(distribution?.deliveryCharges),
      distributionGasCharges: this.mapChargeBucket(distribution?.gasCharges),
      distributionBalancingCharges: this.mapChargeBucket(distribution?.balancingCharges),
      distributionOffPeakCharges: this.mapChargeBucket(distribution?.offPeakCharges),
      distributionRateClassCharges: this.mapChargeBucket(distribution?.rateClassCharges),
      distributionGrtSut: this.mapChargeBucket(distribution?.grtSut),
      distributionMiscCharges: this.mapChargeBucket(distribution?.misc)
    };
  }

  /**
   * Maps API charge bucket to frontend ChargeGroup
   */
  private mapChargeBucket(bucket: ApiChargeBucket | null | undefined): ChargeGroup | undefined {
    if (!bucket) return undefined;

    const total = bucket.total?.cost?.value ?? 0;
    const rawTotal = bucket.total?.cost?.textValue ?? undefined;
    const confidence = bucket.total?.cost?.confidence;

    const lineItems: ChargeLineItem[] = (bucket.lineItems || []).map(item => this.mapChargeItem(item));

    return {
      total,
      rawTotal,
      confidence,
      lineItems
    };
  }

  /**
   * Maps API charge item to frontend ChargeLineItem
   */
  private mapChargeItem(item: ApiChargeItem): ChargeLineItem {
    return {
      description: item.description?.value ?? '',
      rawDescription: undefined, // StringAiValue doesn't have textValue
      amount: item.cost?.value ?? 0,
      rawAmount: item.cost?.textValue ?? undefined,
      rate: item.rate?.value ?? undefined,
      rawRate: item.rate?.textValue ?? undefined,
      usage: item.usage?.value ?? undefined,
      unit: item.unit?.value ?? undefined,
      rawUnit: undefined, // StringAiValue doesn't have textValue
      confidence: item.cost?.confidence
    };
  }

  /**
   * Calculates overall confidence as average of all non-null field confidences
   */
  private calculateOverallConfidence(confidenceMap: Record<string, number>): number {
    const confidenceValues = Object.values(confidenceMap).filter(c => c !== null && c !== undefined);

    if (confidenceValues.length === 0) {
      return 0;
    }

    const sum = confidenceValues.reduce((acc, val) => acc + val, 0);
    return sum / confidenceValues.length;
  }
}
